import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  AlertTitle,
  TextField
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface AuthenticationFlowsComponentProps {
  onBack: () => void;
}

const AuthenticationFlowsComponent: React.FC<AuthenticationFlowsComponentProps> = ({ onBack }) => {
  const [selectedFlow, setSelectedFlow] = useState<string | null>(null);

  const authFlows = [
    {
      id: "auth-code-pkce",
      title: "Test Auth Code Flow with PKCE",
      description: "Configure and test the authorization code flow with PKCE",
      icon: <CodeIcon color="primary" />,
      implemented: true
    },
    {
      id: "client-credentials",
      title: "Test Client Credentials Flow", 
      description: "Configure and test service-to-service authentication",
      icon: <VpnKeyIcon color="primary" />,
      implemented: true
    },
    {
      id: "hybrid-flow",
      title: "Test Hybrid Flow",
      description: "Configure and test both authentication and authorization in a single flow",
      icon: <SecurityIcon color="secondary" />,
      implemented: false
    },
    {
      id: "implicit-flow",
      title: "Test Implicit Flow",
      description: "Configure and test implicit grant flow (legacy)",
      icon: <SecurityIcon color="secondary" />,
      implemented: false
    }
  ];

  const renderFlowContent = () => {
    if (selectedFlow === "auth-code-pkce") {
      return (
        <Box>
        <Typography variant="h5" gutterBottom color="primary">
            Test Authorization Code Flow with PKCE
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Modern Secure Flow</AlertTitle>
            <Typography variant="body2">
            Authorization Code Flow with PKCE (Proof Key for Code Exchange) is the recommended 
            authentication flow for modern applications, including SPAs and mobile apps. 
            It provides security without requiring client secrets.
            </Typography>
        </Alert>
        
        <Typography variant="body1" paragraph>
            This scenario walks you through testing the Auth Code Flow with PKCE using AppConfig²'s 
            built-in Auth Flow Tester. You'll see how the authorization code is exchanged for 
            tokens using a dynamically generated code verifier and challenge.
        </Typography>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
            What You'll Learn
            </Typography>
            <Typography variant="body2">
            • How PKCE protects against authorization code interception attacks<br/>
            • The complete OAuth 2.1 flow from authorization request to token response<br/>
            • How to validate tokens and examine their claims<br/>
            • Best practices for secure token handling in browser-based applications
            </Typography>
        </Box>

        <Stepper orientation="vertical">
            <Step active>
            <StepLabel>
                <Typography variant="subtitle1">Access the Auth Flow Tester Tool</Typography>
            </StepLabel>
            <StepContent>
                <Typography variant="body2" paragraph>
                First, navigate to the Auth Flow Tester tool in AppConfig²:
                </Typography>
                <List dense>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Tools' in the main sidebar navigation" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the Authentication section, click 'Auth Flow Tester'" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="You'll see a list of applications available for testing" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select the application you want to test with the Auth Code Flow" />
                </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                <Button variant="contained" size="small">Continue</Button>
                </Box>
            </StepContent>
            </Step>

            <Step active>
            <StepLabel>
                <Typography variant="subtitle1">Open the OAuth Flow Tester Interface</Typography>
            </StepLabel>
            <StepContent>
                <Typography variant="body2" paragraph>
                After selecting your application, you'll see the OAuth Flow Tester interface:
                </Typography>
                <List dense>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="The left side shows available authentication flows" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="The right side shows flow status and token details" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify your application details are displayed at the top" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Ensure the app has appropriate platform configuration (SPA or Web+SPA for testing)" />
                </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                <Button variant="contained" size="small">Continue</Button>
                </Box>
            </StepContent>
            </Step>

            <Step active>
            <StepLabel>
                <Typography variant="subtitle1">Start Authorization Code Flow with PKCE</Typography>
            </StepLabel>
            <StepContent>
                <Typography variant="body2" paragraph>
                Now let's test the Auth Code Flow with PKCE:
                </Typography>
                <List dense>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Locate the 'Authorization Code Flow (PKCE)' section" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click to expand the accordion if it's not already open" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Start Auth Code Flow' button" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Watch the Flow Status panel on the right for detailed logging" />
                </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                <Button variant="contained" size="small">Continue</Button>
                </Box>
            </StepContent>
            </Step>

            <Step active>
            <StepLabel>
                <Typography variant="subtitle1">Complete Authentication</Typography>
            </StepLabel>
            <StepContent>
                <Typography variant="body2" paragraph>
                The OAuth flow will now proceed through its steps:
                </Typography>
                <List dense>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                    primary="MSAL will open a popup window for authentication" 
                    secondary="The system generates a PKCE code verifier and challenge automatically"
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                    primary="Sign in with your credentials in the popup" 
                    secondary="Use the account selector to choose the appropriate user"
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                    primary="Observe the authorization code exchange" 
                    secondary="MSAL automatically exchanges the code + verifier for tokens"
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                    primary="View the success message and user information" 
                    secondary="The flow status will show completion and user details"
                    />
                </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                <Button variant="contained" size="small">Continue</Button>
                </Box>
            </StepContent>
            </Step>

            <Step active>
            <StepLabel>
                <Typography variant="subtitle1">Examine the Access Token</Typography>
            </StepLabel>
            <StepContent>
                <Typography variant="body2" paragraph>
                Now let's examine the token that was obtained:
                </Typography>
                <List dense>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the Token Details section, click 'Show Token'" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Review the Token Summary for key information" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Scroll down to see the complete token contents" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                    primary="Pay attention to key claims:" 
                    secondary="aud (audience), scp (scopes), iss (issuer), exp (expiration)"
                    />
                </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                <Button variant="contained" size="small">Continue</Button>
                </Box>
            </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Test Different Scenarios</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Try these additional testing scenarios:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Sign out and test again" 
                      secondary="Click 'Sign Out' and repeat the flow to see the full authentication process"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test with different users" 
                      secondary="Use different accounts to see how user-specific claims change"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test token refresh" 
                      secondary="Try the flow again after tokens expire to see silent renewal"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Compare with other flows" 
                      secondary="Try other available flows to understand the differences"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Key Technical Details - PKCE Flow
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Code Verifier Generation" 
                  secondary="MSAL generates a cryptographically random code verifier (43-128 characters)" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Code Challenge Creation" 
                  secondary="The verifier is SHA256 hashed and Base64URL encoded to create the challenge" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Authorization Request" 
                  secondary="Includes code_challenge and code_challenge_method=S256 parameters" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Token Exchange" 
                  secondary="Code + verifier sent to token endpoint, server validates the challenge" 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Security Benefits & Common Issues
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Prevents Code Interception" 
                  secondary="Even if authorization code is intercepted, it's useless without the verifier" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="No Client Secret Required" 
                  secondary="Safe for public clients like SPAs and mobile apps" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Common Issue: Redirect URI Mismatch" 
                  secondary="Ensure redirect URI in app registration exactly matches your domain" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Common Issue: Popup Blocked" 
                  secondary="Ensure browser allows popups for your AppConfig² domain" 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowRightIcon />}
              onClick={() => setSelectedFlow(null)}
            >
              Back to All Authentication Flows
            </Button>
          </Box>
        </Box>
      );
    }

    if (selectedFlow === "client-credentials") {
      return (
        <Box>
        <Typography variant="h5" gutterBottom color="primary">
            Test Client Credentials Flow
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Service-to-Service Authentication</AlertTitle>
            <Typography variant="body2">
            Client Credentials Flow is used for server-to-server authentication where no user context is needed. 
            This flow requires a client secret and should only be used in secure environments.
            </Typography>
        </Alert>
        
        <Typography variant="body1" paragraph>
            This scenario demonstrates how to test the Client Credentials flow using AppConfig²'s Auth Flow Tester. 
            You'll see how applications authenticate directly using their client credentials to access APIs 
            without user involvement.
        </Typography>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1 }}>
            Before You Start
            </Typography>
            <Typography variant="body2">
            • <strong>Client Secret Required:</strong> You'll need a valid client secret for your application<br/>
            • <strong>Confidential Client:</strong> This flow only works with confidential clients (web apps, APIs)<br/>
            • <strong>Application Permissions:</strong> The app should have application permissions, not delegated permissions<br/>
            • <strong>Admin Consent:</strong> Application permissions typically require admin consent
            </Typography>
        </Box>

        <Stepper orientation="vertical">
            <Step active>
            <StepLabel>
                <Typography variant="subtitle1">Access the Auth Flow Tester Tool</Typography>
            </StepLabel>
            <StepContent>
                <Typography variant="body2" paragraph>
                Navigate to the Auth Flow Tester and select your application:
                </Typography>
                <List dense>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Tools' in the main sidebar navigation" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the Authentication section, click 'Auth Flow Tester'" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Find your application in the list (should be configured as Web or API application)" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Test Application' to open the OAuth Flow Tester" />
                </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                <Button variant="contained" size="small">Continue</Button>
                </Box>
            </StepContent>
            </Step>

            <Step active>
            <StepLabel>
                <Typography variant="subtitle1">Access Client Credentials Flow Section</Typography>
            </StepLabel>
            <StepContent>
                <Typography variant="body2" paragraph>
                In the OAuth Flow Tester interface:
                </Typography>
                <List dense>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Locate the 'Client Credentials Flow' accordion section in the Authentication Flows card" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click to expand the Client Credentials Flow section" />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify your application details are correct at the top of the page" />
                </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                <Button variant="contained" size="small">Continue</Button>
                </Box>
            </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Configure Client Secret</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Enter your application's client secret:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Expand the 'Client Credentials Flow' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Locate the 'Client Secret' text field" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Paste your application's client secret value" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Ensure the secret is valid and not expired" 
                      secondary="You can generate a new secret in the Azure portal if needed"
                    />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#fff8e6', borderRadius: 1, border: '1px solid #ffd87c' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Security Note
                  </Typography>
                  <Typography variant="body2">
                    Client secrets are sensitive credentials. In production, store them securely using 
                    Azure Key Vault or similar secret management services.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Execute Client Credentials Flow</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Now let's run the Client Credentials flow:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Start Client Credentials Flow' button" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Watch the Flow Status panel for detailed logging" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Observe the tenant discovery process" 
                      secondary="The system determines the correct tenant for token requests"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Review the token request and response" 
                      secondary="No user interaction required - this is pure service-to-service auth"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Analyze the Access Token</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Examine the service-to-service access token:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the Token Details section, click 'Show Token'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Note the absence of user-specific claims" 
                      secondary="No 'name', 'upn', or user identity claims in service tokens"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Check the 'roles' claim for application permissions" 
                      secondary="Application permissions appear as 'roles', not 'scp' (scopes)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Verify the audience and issuer claims" 
                      secondary="Ensure token is issued for the correct API/resource"
                    />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Expected Token Structure
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {`{
      "aud": "https://graph.microsoft.com",
      "iss": "https://sts.windows.net/{tenant-id}/",
      "appid": "{your-app-id}",
      "roles": ["Application.ReadWrite.All"],
      "sub": "{app-object-id}",
      ...
    }`}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>
                <Typography variant="subtitle1">Test and Troubleshoot</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Test various scenarios and troubleshoot common issues:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test with different client secrets" 
                      secondary="Try expired or invalid secrets to see error handling"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test token expiration and renewal" 
                      secondary="Client credentials tokens typically last 60-90 minutes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Verify application permissions" 
                      secondary="Check that required permissions are granted and consented"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Clear the token and test again" 
                      secondary="Use 'Exit Client Creds Flow' to reset and retest"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Client Credentials Flow Characteristics
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="No User Context" 
                  secondary="Application acts on its own behalf, not on behalf of a user" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Application Permissions Only" 
                  secondary="Uses application permissions (roles), not delegated permissions (scopes)" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Admin Consent Required" 
                  secondary="Application permissions require administrator consent" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Long-lived Tokens" 
                  secondary="Tokens typically last 60-90 minutes and can be cached" 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Common Issues & Solutions
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="unauthorized_client error" 
                  secondary="Application not found in tenant or client secret invalid/expired" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="insufficient_claims error" 
                  secondary="Application lacks required permissions or admin consent not granted" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Tenant discovery failures" 
                  secondary="Ensure application is registered in the correct tenant" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Network/CORS issues" 
                  secondary="Client credentials should typically be used server-side, not in browsers" 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowRightIcon />}
              onClick={() => setSelectedFlow(null)}
            >
              Back to All Authentication Flows
            </Button>
          </Box>
        </Box>
      );
    }

    // Placeholder flows
    if (selectedFlow === "hybrid-flow" || selectedFlow === "implicit-flow") {
      const flowName = selectedFlow === "hybrid-flow" ? "Hybrid Flow" : "Implicit Flow";
      const flowDescription = selectedFlow === "hybrid-flow" 
        ? "The Hybrid Flow combines both authentication and authorization in a single request, returning both an ID token and authorization code."
        : "The Implicit Flow returns tokens directly in the authorization response without a token exchange step.";

      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Test {flowName}
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Coming Soon</AlertTitle>
            <Typography variant="body2">
              This authentication flow testing scenario is currently under development and will be available in a future update.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            {flowDescription}
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ContentCopyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {flowName} Overview
                </Typography>
              </Box>
              
              <Typography variant="body2" paragraph>
                This scenario will cover:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={`Step-by-step ${flowName.toLowerCase()} configuration`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Token examination and validation" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Security considerations and best practices" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Common troubleshooting scenarios" />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              What to Expect
            </Typography>
            <Typography variant="body2">
              When this scenario is completed, you'll have comprehensive guidance for testing {flowName.toLowerCase()} 
              using AppConfig²'s OAuth Flow Tester, including detailed explanations of the security implications 
              and appropriate use cases for this authentication method.
            </Typography>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowRightIcon />}
              onClick={() => setSelectedFlow(null)}
            >
              Back to All Authentication Flows
            </Button>
          </Box>
        </Box>
      );
    }

    // Default case - should not happen
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Flow not found
        </Typography>
        <Button 
          variant="outlined"
          onClick={() => setSelectedFlow(null)}
        >
          Back to All Authentication Flows
        </Button>
      </Box>
    );
  };

  // If a flow is selected, show its content
  if (selectedFlow) {
    return renderFlowContent();
  }

  // Otherwise, show the list of authentication flows
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Authentication Flows
      </Typography>
      
      <Typography variant="body1" paragraph color="text.secondary">
        Select an authentication flow below to view step-by-step instructions for testing 
        different OAuth 2.0 and OpenID Connect flows using AppConfig²'s built-in OAuth Flow Tester.
      </Typography>
      
      <Grid container spacing={2}>
        {authFlows.map((flow) => (
          <Grid item xs={12} md={6} key={flow.id}>
            <Card 
              variant="outlined"
              sx={{ 
                cursor: flow.implemented ? 'pointer' : 'default',
                opacity: flow.implemented ? 1 : 0.6,
                '&:hover': flow.implemented ? {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                } : {},
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={() => flow.implemented && setSelectedFlow(flow.id)}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {flow.icon}
                  <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
                    {flow.title}
                  </Typography>
                  {!flow.implemented && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        ml: 'auto', 
                        bgcolor: 'grey.200', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1,
                        color: 'text.secondary'
                      }}
                    >
                      Coming Soon
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {flow.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 4 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowRightIcon />}
          onClick={onBack}
        >
          Back to Test Scenarios
        </Button>
      </Box>
    </Box>
  );
};

export default AuthenticationFlowsComponent;